import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface BottomNavItem {
  href: string;
  label: string;
  icon: React.ReactNode;
  /** If true, renders as FAB-style prominent button */
  primary?: boolean;
}

interface BottomNavProps {
  items: BottomNavItem[];
  /** Callback for menu button click */
  onMenuClick?: () => void;
  className?: string;
}

// Icon components
const DashboardIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const DealsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
  </svg>
);

const CommissionsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const MenuIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

export function BottomNav({ items, onMenuClick, className }: BottomNavProps) {
  const location = useLocation();

  const isActive = (href: string) => {
    if (href.startsWith('/admin/')) {
      return location.pathname.startsWith(href);
    }
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <nav className={cn('bottom-nav', className)}>
      {items.map((item, index) => {
        const active = isActive(item.href);

        if (item.primary) {
          // FAB-style primary action
          return (
            <Link
              key={item.href}
              to={item.href}
              className="bottom-nav-fab"
              aria-label={item.label}
            >
              {item.icon}
            </Link>
          );
        }

        // Check if this is the menu item (last item triggers onMenuClick)
        if (index === items.length - 1 && onMenuClick) {
          return (
            <button
              key="menu"
              type="button"
              onClick={onMenuClick}
              className={cn('bottom-nav-item', active && 'active')}
              aria-label={item.label}
            >
              {item.icon}
              <span className="text-[10px]">{item.label}</span>
            </button>
          );
        }

        return (
          <Link
            key={item.href}
            to={item.href}
            className={cn('bottom-nav-item', active && 'active')}
            aria-label={item.label}
          >
            {item.icon}
            <span className="text-[10px]">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

// Pre-configured bottom nav for partner users
export function PartnerBottomNav({ onMenuClick }: { onMenuClick?: () => void }) {
  const items: BottomNavItem[] = [
    { href: '/dashboard', label: 'Home', icon: <DashboardIcon /> },
    { href: '/deals', label: 'Deals', icon: <DealsIcon /> },
    { href: '/deals/new', label: 'New', icon: <PlusIcon />, primary: true },
    { href: '/commissions', label: 'Commissions', icon: <CommissionsIcon /> },
    { href: '#menu', label: 'Menu', icon: <MenuIcon /> },
  ];

  return <BottomNav items={items} onMenuClick={onMenuClick} />;
}

// Pre-configured bottom nav for admin users
export function AdminBottomNav({ onMenuClick }: { onMenuClick?: () => void }) {
  const items: BottomNavItem[] = [
    { href: '/admin/dashboard', label: 'Home', icon: <DashboardIcon /> },
    { href: '/admin/deals', label: 'Deals', icon: <DealsIcon /> },
    { href: '/deals/new', label: 'New', icon: <PlusIcon />, primary: true },
    { href: '/admin/commissions', label: 'Commissions', icon: <CommissionsIcon /> },
    { href: '#menu', label: 'Menu', icon: <MenuIcon /> },
  ];

  return <BottomNav items={items} onMenuClick={onMenuClick} />;
}
